import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { analyseDocument, computeDocumentFlags, isAiConfigured } from '../../../services/aiDocumentService';
import { documentService } from '../../../services/documentService';
import { useLanguage } from '../../../contexts/LanguageContext';

const CATEGORY_OPTIONS = [
  { value: 'rams', label: 'H&S Docs' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'training', label: 'Training Certificates' },
  { value: 'plant', label: 'Plant Certificates' },
  { value: 'inspection_report', label: 'Inspection Report' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'equipment_certificate', label: 'Equipment Certificate' }
];

function ConfidenceBadge({ value }) {
  if (value == null) return null;
  const pct = Math.round((value || 0) * 100);
  const color = pct >= 80 ? 'text-success' : pct >= 50 ? 'text-warning' : 'text-muted-foreground';
  return (
    <span className={`text-xs font-medium ml-1 ${color}`}>{pct}% confidence</span>
  );
}

function FlagsPanel({ flags }) {
  if (!flags?.length) return null;
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-foreground">Document Flags</p>
      {flags?.map((flag, i) => (
        <div
          key={i}
          className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
            flag?.severity === 'error' ?'bg-error/10 border border-error/20 text-error' :'bg-warning/10 border border-warning/20 text-warning'
          }`}
        >
          <Icon
            name={flag?.severity === 'error' ? 'AlertCircle' : 'AlertTriangle'}
            size={14}
            className="flex-shrink-0"
          />
          {flag?.label}
        </div>
      ))}
    </div>
  );
}

const DocumentUploadModal = ({ isOpen, onClose, onUpload, sites, defaultSiteId }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    owner: '',
    issueDate: '',
    expiryDate: '',
    notes: '',
    siteId: defaultSiteId || '',
    certificateNumber: '',
    policyNumber: '',
    contractorName: '',
    inspectionDate: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatusLabel, setUploadStatusLabel] = useState('');

  // AI state
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [aiDismissed, setAiDismissed] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [confidence, setConfidence] = useState({});
  const [flags, setFlags] = useState([]);
  const [existingDocs, setExistingDocs] = useState([]);

  const { t } = useLanguage();
  const siteOptions = sites?.map(s => ({ value: s?.id, label: s?.site_name })) || [];

  // Load existing docs for duplicate check when site changes
  useEffect(() => {
    if (!formData?.siteId) return;
    documentService?.getDocumentsBySite(formData?.siteId)?.then(({ data }) => {
      setExistingDocs(data || []);
    });
  }, [formData?.siteId]);

  // Recompute flags whenever relevant fields change
  useEffect(() => {
    setFlags(computeDocumentFlags(formData, existingDocs));
  }, [formData?.expiryDate, formData?.title, formData?.certificateNumber, existingDocs]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleFileChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const validTypes = [
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg', 'image/png', 'image/jpg'
    ];
    if (!validTypes?.includes(file?.type)) {
      setErrors(prev => ({ ...prev, file: 'Invalid file type. Only PDF, DOC, DOCX, JPG, PNG allowed.' }));
      return;
    }
    if (file?.size > 10 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, file: 'File size must be less than 10MB.' }));
      return;
    }
    setSelectedFile(file);
    setErrors(prev => ({ ...prev, file: '' }));

    // Auto-populate title from filename
    const nameWithoutExt = file?.name?.replace(/\.[^/.]+$/, '');
    if (!formData?.title) handleChange('title', nameWithoutExt);

    // AI autofill currently runs on images only. OpenAI's Chat Completions
    // API accepts `image_url` content parts but NOT the Responses-API
    // `file` part that PDFs would need; supporting PDFs is a separate follow
    // up (client-side PDF→image, or a new edge function using the Responses
    // API). Users uploading PDFs still get the rest of the form and a clear
    // caption explaining why autofill didn't run.
    const isPdf = file?.type === 'application/pdf';
    const canAnalyse = file?.type?.startsWith('image/');
    if (canAnalyse && isAiConfigured()) {
      setIsAnalysing(true);
      setAiError(null);
      setConfidence({});
      const { suggestions, confidence: conf, error } = await analyseDocument(file);
      setIsAnalysing(false);

      if (error) {
        setAiError(error);
      } else if (suggestions) {
        setConfidence(conf || {});
        setFormData(prev => ({
          ...prev,
          title: suggestions?.title || prev?.title || nameWithoutExt,
          category: suggestions?.category || prev?.category,
          issueDate: suggestions?.issue_date || prev?.issueDate,
          expiryDate: suggestions?.expiry_date || prev?.expiryDate,
          certificateNumber: suggestions?.certificate_number || prev?.certificateNumber,
          policyNumber: suggestions?.policy_number || prev?.policyNumber,
          contractorName: suggestions?.contractor_name || prev?.contractorName,
          inspectionDate: suggestions?.inspection_date || prev?.inspectionDate,
          notes: suggestions?.site_name
            ? `Site: ${suggestions?.site_name}${prev?.notes ? '\n' + prev?.notes : ''}`
            : prev?.notes
        }));
      }
    } else if (isPdf) {
      setAiError('AI autofill supports image files. PDFs can be filled in manually.');
    } else if (!isAiConfigured()) {
      setAiError('not_configured');
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!selectedFile) newErrors.file = 'Please select a file.';
    if (!formData?.title?.trim()) newErrors.title = 'Document title is required.';
    if (!formData?.category) newErrors.category = 'Category is required.';
    if (!formData?.siteId) newErrors.siteId = 'Site is required.';
    if (!formData?.issueDate) newErrors.issueDate = 'Issue date is required.';
    if (!formData?.expiryDate) newErrors.expiryDate = 'Expiry date is required.';
    if (formData?.issueDate && formData?.expiryDate && new Date(formData?.expiryDate) <= new Date(formData?.issueDate)) {
      newErrors.expiryDate = 'Expiry date must be after issue date.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatusLabel('Uploading...');
    try {
      await onUpload({
        ...formData,
        file: selectedFile,
        onProgress: (pct) => {
          setUploadProgress(pct);
          if (pct < 95) setUploadStatusLabel('Uploading file...');
          else if (pct < 100) setUploadStatusLabel('Saving record...');
          else setUploadStatusLabel('Done!');
        }
      });
      handleClose();
    } catch (err) {
      console.error('[DocumentUploadModal] upload failed:', err);
      setErrors(prev => ({ ...prev, submit: 'Upload failed. Please try again.' }));
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatusLabel('');
    }
  };

  const handleClose = () => {
    setFormData({ title: '', category: '', owner: '', issueDate: '', expiryDate: '', notes: '', siteId: defaultSiteId || '', certificateNumber: '', policyNumber: '', contractorName: '', inspectionDate: '' });
    setSelectedFile(null);
    setErrors({});
    setIsUploading(false);
    setIsAnalysing(false);
    setAiError(null);
    setConfidence({});
    setFlags([]);
    setAiDismissed(false);
    onClose();
  };

  if (!isOpen) return null;

  const showAiNotice = !aiDismissed && aiError === 'not_configured';
  const showAiFailed = !aiDismissed && aiError && aiError !== 'not_configured';
  const hasConfidence = Object.keys(confidence)?.length > 0;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
              <Icon name="Upload" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{t('uploadDocument')}</h2>
              <p className="text-sm text-muted-foreground">{t('uploadDocumentSubtitle')}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose} disabled={isUploading} aria-label={t('close')}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">

          {/* AI unavailable notice */}
          {showAiNotice && (
            <div className="flex items-start gap-3 p-3 bg-muted/50 border border-border rounded-md">
              <Icon name="Info" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground flex-1">{t('aiAutofillUnavailable')}</p>
              <button type="button" onClick={() => setAiDismissed(true)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={14} />
              </button>
            </div>
          )}

          {/* AI analysis failed notice — show the real error so PDF hints
               and OpenAI failures don't collapse to the same generic copy. */}
          {showAiFailed && (
            <div className="flex items-start gap-3 p-3 bg-muted/50 border border-border rounded-md">
              <Icon name="AlertCircle" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground flex-1">{aiError}</p>
              <button type="button" onClick={() => setAiDismissed(true)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={14} />
              </button>
            </div>
          )}

          {/* File picker */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('file')} <span className="text-error">*</span>
            </label>
            <input
              id="doc-upload-file-input"
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="hidden"
              disabled={isUploading}
            />
            <button
              type="button"
              onClick={() => document?.getElementById('doc-upload-file-input')?.click()}
              className="w-full px-4 py-3 bg-background border-2 border-dashed border-input rounded-md text-left hover:border-accent transition-smooth flex items-center gap-3"
              disabled={isUploading}
            >
              <Icon name="Upload" size={20} color="var(--color-accent)" />
              <div className="flex-1">
                {selectedFile ? (
                  <div>
                    <p className="text-sm font-medium text-foreground">{selectedFile?.name}</p>
                    <p className="text-xs text-muted-foreground">{(selectedFile?.size / 1024 / 1024)?.toFixed(2)} MB</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-foreground">{t('clickToSelectFile')}</p>
                    <p className="text-xs text-muted-foreground">{t('pdfDocJpgPngMax10mb')}</p>
                  </div>
                )}
              </div>
            </button>
            {errors?.file && <p className="text-sm text-error mt-1">{errors?.file}</p>}
          </div>

          {/* AI analysing state */}
          {isAnalysing && (
            <div className="flex items-center gap-3 p-3 bg-accent/5 border border-accent/20 rounded-md">
              <Icon name="Loader2" size={16} className="text-accent animate-spin flex-shrink-0" />
              <p className="text-sm text-accent">{t('analysingDocument')}</p>
            </div>
          )}

          {/* AI autofill banner */}
          {hasConfidence && !isAnalysing && (
            <div className="flex items-center gap-2 p-3 bg-accent/5 border border-accent/20 rounded-md">
              <Icon name="Sparkles" size={16} className="text-accent flex-shrink-0" />
              <p className="text-sm text-accent">{t('aiPrefilledFields')}</p>
            </div>
          )}

          {/* Title */}
          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="text-sm font-medium text-foreground">{t('documentTitle')} <span className="text-error">*</span></label>
              {hasConfidence && <ConfidenceBadge value={confidence?.title} />}
            </div>
            <Input
              type="text"
              placeholder={t('enterDocumentTitle')}
              value={formData?.title}
              onChange={(e) => handleChange('title', e?.target?.value)}
              error={errors?.title}
              required
              disabled={isUploading}
            />
          </div>

          {/* Category + Site */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="text-sm font-medium text-foreground">{t('category')} <span className="text-error">*</span></label>
                {hasConfidence && <ConfidenceBadge value={confidence?.category} />}
              </div>
              <Select
                options={CATEGORY_OPTIONS}
                value={formData?.category}
                onChange={(value) => handleChange('category', value)}
                error={errors?.category}
                required
                disabled={isUploading}
              />
            </div>
            <Select
              label={t('site')}
              options={siteOptions}
              value={formData?.siteId}
              onChange={(value) => handleChange('siteId', value)}
              error={errors?.siteId}
              required
              disabled={isUploading}
            />
          </div>

          {/* Owner */}
          <Input
            label={t('ownerResponsibleParty')}
            type="text"
            placeholder={t('enterOwnerName')}
            value={formData?.owner}
            onChange={(e) => handleChange('owner', e?.target?.value)}
            disabled={isUploading}
          />

          {/* Contractor Name */}
          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="text-sm font-medium text-foreground">{t('contractorCompanyName')}</label>
              {hasConfidence && <ConfidenceBadge value={confidence?.contractor_name} />}
            </div>
            <Input
              type="text"
              placeholder={t('contractorOrCompanyName')}
              value={formData?.contractorName}
              onChange={(e) => handleChange('contractorName', e?.target?.value)}
              disabled={isUploading}
            />
          </div>

          {/* Certificate + Policy numbers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="text-sm font-medium text-foreground">{t('certificateNumber')}</label>
                {hasConfidence && <ConfidenceBadge value={confidence?.certificate_number} />}
              </div>
              <Input
                type="text"
                placeholder="e.g. CERT-2024-001"
                value={formData?.certificateNumber}
                onChange={(e) => handleChange('certificateNumber', e?.target?.value)}
                disabled={isUploading}
              />
            </div>
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="text-sm font-medium text-foreground">{t('policyNumber')}</label>
                {hasConfidence && <ConfidenceBadge value={confidence?.policy_number} />}
              </div>
              <Input
                type="text"
                placeholder="e.g. POL-2024-001"
                value={formData?.policyNumber}
                onChange={(e) => handleChange('policyNumber', e?.target?.value)}
                disabled={isUploading}
              />
            </div>
          </div>

          {/* Issue + Expiry dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="text-sm font-medium text-foreground">{t('issueDate')} <span className="text-error">*</span></label>
                {hasConfidence && <ConfidenceBadge value={confidence?.issue_date} />}
              </div>
              <Input
                type="date"
                value={formData?.issueDate}
                onChange={(e) => handleChange('issueDate', e?.target?.value)}
                error={errors?.issueDate}
                required
                disabled={isUploading}
              />
            </div>
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="text-sm font-medium text-foreground">{t('expiryDate')} <span className="text-error">*</span></label>
                {hasConfidence && <ConfidenceBadge value={confidence?.expiry_date} />}
              </div>
              <Input
                type="date"
                value={formData?.expiryDate}
                onChange={(e) => handleChange('expiryDate', e?.target?.value)}
                error={errors?.expiryDate}
                required
                disabled={isUploading}
              />
            </div>
          </div>

          {/* Inspection Date */}
          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="text-sm font-medium text-foreground">{t('inspectionDate')}</label>
              {hasConfidence && <ConfidenceBadge value={confidence?.inspection_date} />}
            </div>
            <Input
              type="date"
              value={formData?.inspectionDate}
              onChange={(e) => handleChange('inspectionDate', e?.target?.value)}
              disabled={isUploading}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('notes')}</label>
            <textarea
              placeholder={t('optionalNotesAboutDocument')}
              value={formData?.notes}
              onChange={(e) => handleChange('notes', e?.target?.value)}
              rows={3}
              disabled={isUploading}
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-none disabled:opacity-50"
            />
          </div>

          {/* Document Flags */}
          <FlagsPanel flags={flags} />

          {errors?.submit && (
            <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-md">
              <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
              <p className="text-sm text-error">{errors?.submit}</p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex flex-col gap-3 p-4 md:p-6 border-t border-border">
          {isUploading && (
            <div className="w-full">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">{uploadStatusLabel || 'Uploading...'}</span>
                <span className="text-xs text-muted-foreground">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-accent h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <Button variant="outline" onClick={handleClose} disabled={isUploading}>{t('cancel')}</Button>
            <Button onClick={handleSubmit} disabled={isUploading || isAnalysing} iconName={isUploading ? 'Loader2' : 'Upload'} iconPosition="left">
              {isUploading ? uploadStatusLabel || t('uploading') : t('uploadDocument')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
